import React, { useRef, useState, useEffect } from 'react';
import { Screenshot } from '../types';
import NavigationIndicator, { IndicatorType } from './NavigationIndicator';
import './ScreenshotDisplay.css';

interface ScreenshotDisplayProps {
  screenshot: Screenshot;
  nextScreenshot: Screenshot | null;
  navigationIndicator?: IndicatorType;
  indicatorKey?: number;
  onIndicatorComplete?: () => void;
}

const ScreenshotDisplay: React.FC<ScreenshotDisplayProps> = ({
  screenshot,
  nextScreenshot,
  navigationIndicator,
  indicatorKey,
  onIndicatorComplete
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });

  useEffect(() => {
    const updateScale = () => {
      if (imgRef.current && containerRef.current) {
        const img = imgRef.current;
        
        // Get the actual displayed size of the image
        const displayedWidth = img.clientWidth;
        const displayedHeight = img.clientHeight;
        
        // Get the natural (original) size of the image
        const naturalWidth = img.naturalWidth;
        const naturalHeight = img.naturalHeight;
        
        // Calculate scale factors
        const scaleX = displayedWidth / naturalWidth;
        const scaleY = displayedHeight / naturalHeight;

        setScale({ x: scaleX, y: scaleY });
      }
    };

    // Update scale when image loads or window resizes
    const img = imgRef.current;
    if (img) {
      img.addEventListener('load', updateScale);
      window.addEventListener('resize', updateScale);
      
      // Also update if image is already loaded
      if (img.complete) {
        updateScale();
      }
    }

    return () => {
      if (img) {
        img.removeEventListener('load', updateScale);
        window.removeEventListener('resize', updateScale);
      }
    };
  }, [screenshot?.screenshot]);

  const getClickPosition = () => {
    if (!screenshot?.input || !screenshot.input.coordinate || !imgRef.current || !containerRef.current) {
      return null;
    }

    const img = imgRef.current;
    const container = containerRef.current;
    
    // Get the position of the image relative to its container
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate the offset of the image within the container
    const offsetX = imgRect.left - containerRect.left;
    const offsetY = imgRect.top - containerRect.top;
    
    // Apply scale to coordinates and add the offset
    const scaledX = screenshot.input.coordinate[0] * scale.x + offsetX;
    const scaledY = screenshot.input.coordinate[1] * scale.y + offsetY;

    return {
      left: `${scaledX}px`,
      top: `${scaledY}px`,
    };
  };

  const getNextClickPosition = () => {
    if (!nextScreenshot || !imgRef.current || !containerRef.current) {
      return null;
    }

    // Check if next action is a click action
    const isClickAction = nextScreenshot.action === 'click' || 
                         nextScreenshot.action === 'left_click' || 
                         nextScreenshot.action === 'right_click' ||
                         nextScreenshot.action === 'triple_click';
    
    if (!isClickAction || !nextScreenshot.input || !nextScreenshot.input.coordinate) {
      return null;
    }

    const img = imgRef.current;
    const container = containerRef.current;
    
    // Get the position of the image relative to its container
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate the offset of the image within the container
    const offsetX = imgRect.left - containerRect.left;
    const offsetY = imgRect.top - containerRect.top;
    
    // Apply scale to coordinates and add the offset
    const scaledX = nextScreenshot.input.coordinate[0] * scale.x + offsetX;
    const scaledY = nextScreenshot.input.coordinate[1] * scale.y + offsetY;

    return {
      left: `${scaledX}px`,
      top: `${scaledY}px`,
    };
  };

  const showClickIndicator = 
    (screenshot?.action === 'click' || screenshot?.action === 'left_click' || screenshot?.action === 'right_click' || screenshot?.action === 'triple_click') &&
    screenshot?.input && screenshot.input.coordinate;

  const showDragIndicator = (screenshot?.action === 'drag' || screenshot?.action === 'left_click_drag') && 
    screenshot?.input && screenshot.input.startCoordinate && screenshot.input.endCoordinate;

  const clickPosition = getClickPosition();
  const isRightClick = screenshot?.action === 'right_click';
  const isTripleClick = screenshot?.action === 'triple_click';

  const getDragPositions = () => {
    if (!screenshot?.input || !screenshot.input.startCoordinate || !screenshot.input.endCoordinate || !imgRef.current || !containerRef.current) {
      return null;
    }

    const img = imgRef.current;
    const container = containerRef.current;
    
    // Get the position of the image relative to its container
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate the offset of the image within the container
    const offsetX = imgRect.left - containerRect.left;
    const offsetY = imgRect.top - containerRect.top;

    const startX = screenshot.input.startCoordinate[0] * scale.x + offsetX;
    const startY = screenshot.input.startCoordinate[1] * scale.y + offsetY;
    const endX = screenshot.input.endCoordinate[0] * scale.x + offsetX;
    const endY = screenshot.input.endCoordinate[1] * scale.y + offsetY;

    return {
      start: { x: startX, y: startY },
      end: { x: endX, y: endY },
      width: Math.abs(endX - startX),
      height: Math.abs(endY - startY),
      left: Math.min(startX, endX),
      top: Math.min(startY, endY),
    };
  };

  const dragPositions = showDragIndicator ? getDragPositions() : null;
  const nextClickPosition = getNextClickPosition();

  // Guard against undefined screenshot
  if (!screenshot || !screenshot.screenshot) {
    return (
      <div className="screenshot-display">
        <div className="screenshot-container">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: '#666'
          }}>
            No screenshot available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screenshot-display">
      <div className="screenshot-container" ref={containerRef}>
        <img
          ref={imgRef}
          src={screenshot.screenshot.startsWith('data:') ? screenshot.screenshot : `data:image/png;base64,${screenshot.screenshot}`}
          alt={`Screenshot ${screenshot.index + 1}`}
          className="screenshot-image"
        />
        {showClickIndicator && clickPosition && (
          <div
            className={`click-indicator ${isRightClick ? 'right-click' : isTripleClick ? 'triple-click' : 'left-click'}`}
            style={clickPosition}
          >
            {isTripleClick ? (
              <>
                <div className="click-ripple triple-1" />
                <div className="click-ripple triple-2" />
                <div className="click-ripple triple-3" />
                <div className="click-label">3x</div>
              </>
            ) : (
              <>
                <div className="click-ripple" />
                {isRightClick && <div className="click-label">R</div>}
              </>
            )}
          </div>
        )}
        {nextClickPosition && (
          <div
            className="next-click-indicator"
            style={nextClickPosition}
          >
            <div className="next-click-preview" />
          </div>
        )}
        {showDragIndicator && dragPositions && (
          <>
            <div 
              className="drag-area"
              style={{
                left: `${dragPositions.left}px`,
                top: `${dragPositions.top}px`,
                width: `${dragPositions.width}px`,
                height: `${dragPositions.height}px`,
              }}
            />
            <div 
              className="drag-start"
              style={{
                left: `${dragPositions.start.x}px`,
                top: `${dragPositions.start.y}px`,
              }}
            >
              <div className="drag-point" />
            </div>
            <div 
              className="drag-end"
              style={{
                left: `${dragPositions.end.x}px`,
                top: `${dragPositions.end.y}px`,
              }}
            >
              <div className="drag-point" />
              <div className="drag-arrow">→</div>
            </div>
            <svg 
              className="drag-line"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#667eea"
                  />
                </marker>
              </defs>
              <line
                x1={dragPositions.start.x}
                y1={dragPositions.start.y}
                x2={dragPositions.end.x}
                y2={dragPositions.end.y}
                stroke="#667eea"
                strokeWidth="2"
                strokeDasharray="5,5"
                markerEnd="url(#arrowhead)"
              />
            </svg>
          </>
        )}
        <NavigationIndicator 
          key={indicatorKey}
          type={navigationIndicator || null} 
          onComplete={onIndicatorComplete} 
        />
      </div>
    </div>
  );
};

export default ScreenshotDisplay;